<script setup>
import { useI18n } from 'vue-i18n';

// Renders blocking errors (must fix before save) and non-blocking warnings as
// plain rows. A row that carries a screenId offers «روح للمشكلة» which asks the
// parent to scroll to and select that screen.
defineProps({
  errors: { type: Array, default: () => [] }, // [{ text, id? }]
  warnings: { type: Array, default: () => [] }, // [{ text, id? }]
});
const emit = defineEmits(['jump']);
const { t } = useI18n();
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="errors.length"
      class="rounded-xl border border-n-ruby-6 bg-n-ruby-2 p-4"
    >
      <p
        class="mb-2 flex items-center gap-1.5 text-[13px] font-extrabold text-n-ruby-11"
      >
        <span class="i-lucide-octagon-alert size-4" />
        {{ t('AUTO_REPLY_BOT_SETTINGS.ISSUES.TITLE_ERRORS') }}
      </p>
      <ul class="flex flex-col gap-1.5">
        <li
          v-for="(err, i) in errors"
          :key="i"
          class="flex items-center justify-between gap-3 text-[12.5px] font-medium text-n-ruby-11"
        >
          <span class="flex items-start gap-1.5">
            <span class="i-lucide-dot -ms-1 size-4 shrink-0" />
            {{ err.text }}
          </span>
          <button
            v-if="err.id"
            type="button"
            class="shrink-0 whitespace-nowrap rounded-lg border border-n-ruby-6 px-2 py-1 text-[11px] font-bold text-n-ruby-11 transition-colors hover:bg-n-ruby-3"
            @click="emit('jump', err.id)"
          >
            {{ t('AUTO_REPLY_BOT_SETTINGS.ISSUES.JUMP') }}
          </button>
        </li>
      </ul>
    </div>

    <div
      v-if="warnings.length"
      class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-4"
    >
      <p
        class="mb-2 flex items-center gap-1.5 text-[13px] font-extrabold text-n-amber-11"
      >
        <span class="i-lucide-triangle-alert size-4" />
        {{ t('AUTO_REPLY_BOT_SETTINGS.ISSUES.TITLE_WARNINGS') }}
      </p>
      <ul class="flex flex-col gap-1.5">
        <li
          v-for="(warn, i) in warnings"
          :key="i"
          class="flex items-center justify-between gap-3 text-[12.5px] font-medium text-n-amber-11"
        >
          <span class="flex items-start gap-1.5">
            <span class="i-lucide-dot -ms-1 size-4 shrink-0" />
            {{ warn.text }}
          </span>
          <button
            v-if="warn.id"
            type="button"
            class="shrink-0 whitespace-nowrap rounded-lg border border-n-amber-6 px-2 py-1 text-[11px] font-bold text-n-amber-11 transition-colors hover:bg-n-amber-3"
            @click="emit('jump', warn.id)"
          >
            {{ t('AUTO_REPLY_BOT_SETTINGS.ISSUES.JUMP') }}
          </button>
        </li>
      </ul>
    </div>

    <div
      v-if="!errors.length && !warnings.length"
      class="flex items-center gap-2 rounded-xl border border-n-teal-6 bg-n-teal-2 px-4 py-3 text-[12.5px] font-bold text-n-teal-11"
    >
      <span class="i-lucide-circle-check size-4" />
      {{ t('AUTO_REPLY_BOT_SETTINGS.ISSUES.ALL_GOOD') }}
    </div>
  </div>
</template>
